import { useLayoutEffect, useRef, useState } from "react";
import { EditorialArtwork } from "./EditorialArtwork";

import { failedCovers, loadedCovers } from "../lib/coverCache";
import { imageSource, imageSourceSet } from "../lib/responsiveImage";

type StoryCoverProps = {
  src: string;
  alt: string;
  title: string;
  loading?: "eager" | "lazy";
  className?: string;
  sizes?: string;
};

/**
 * A cover keeps its place while the network image is decoding, instead of
 * briefly exposing an empty rectangle. The fallback is deliberately part of
 * the archive's visual language, so it can also remain on a failed request.
 */
export function StoryCover({ src, alt, title, loading = "lazy", className = "", sizes = "(max-width: 700px) 100vw, 600px" }: StoryCoverProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">(() => loadedCovers.has(src) ? "ready" : failedCovers.has(src) ? "error" : "loading");
  const [cached, setCached] = useState(() => loadedCovers.has(src));

  useLayoutEffect(() => {
    const image = imageRef.current;
    const ready = loadedCovers.has(src) || Boolean(image?.complete && image.naturalWidth > 0);
    setCached(ready);
    setState(ready ? "ready" : failedCovers.has(src) ? "error" : "loading");
  }, [src]);

  return <figure className={`card-cover cover-${state} ${cached ? "cover-cached" : ""} ${className}`.trim()} aria-busy={state === "loading" || undefined}>
    <span className="cover-placeholder" aria-hidden="true">
      <EditorialArtwork seed={title} />
      <span className="cover-placeholder-caption"><i /> {state === "error" ? "Обложка недоступна" : "Загружаем обложку"}</span>
    </span>
    <img ref={imageRef} src={imageSource(src)} srcSet={imageSourceSet(src)} sizes={sizes} alt={alt} loading={cached ? "eager" : loading} fetchPriority={loading === "eager" ? "high" : "auto"} decoding="async" draggable={false} onLoad={() => { failedCovers.delete(src); loadedCovers.add(src); setState("ready"); }} onError={() => { loadedCovers.delete(src); failedCovers.add(src); setState("error"); }} />
    <i className="card-cover-glare" aria-hidden="true" />
  </figure>;
}
